import React, { FC, useEffect } from 'react';
import classnames from 'classnames';
import { useHistory } from 'react-router-dom';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import {
  CompanyProfileStateEnum,
  useGetOpeningApplicantCountsLazyQuery,
  useInboxMessageThreadUpdatedSubscription,
  useSessionQuery,
} from '@src/graphql/generated';
import { OpeningCandidateProfile } from '@src/containers/OpeningCandidateProfile';
import { trackEvent } from '@src/metrics';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { createLink } from '@src/util/createLink';
import { isInactive } from '@src/util/opening';
import { FUNNEL_ROUTE } from '@src/Routes/links';
import { FinishCompanyProfileModal } from '@src/components/FinishCompanyProfileModal';
import { QueryParams } from './interfaces/queryParams.interface';
import { Header } from './Header';
import { ActiveView } from './enums/activeView.enum';
import { InviteCandidates } from './InviteCandidates';
import { PendingInvites } from './PendingInvites';
import { InConversation } from './InConversation';
import { useGetOpeningId } from './hooks/useGetOpeningId';
import { InShortlist } from './InShortlist';
import './styles.scss';

export const Opening: FC = () => {
  const { queryParams } = useQueryParams<QueryParams>();

  const openingId = useGetOpeningId();

  const history = useHistory();

  const { data: sessionData } = useSessionQuery();

  const isCompanyProfilePublished = sessionData?.company.profile.state === CompanyProfileStateEnum.PUBLISHED;

  const activeView = queryParams?.activeView || ActiveView.INVITE_CANDIDATES;

  const [
    getOpeningApplicantCounts,
    {
      data: openingApplicantCountsData,
      loading: isOpeningApplicantCountsLoading,
      refetch: refetchOpeningApplicantCounts,
    },
  ] = useGetOpeningApplicantCountsLazyQuery();

  const opening = openingApplicantCountsData?.opening;
  const recommended = opening?.recommendedUsers.pagination.totalCount || 0;
  const totalUnanswered = opening?.pendingApplicants.pagination.totalCount || 0;
  const totalMessaged = opening?.inConversationApplicants.pagination.totalCount || 0;
  const totalShortlist = opening?.shortlistApplicants.pagination.totalCount || 0;
  const unreadMessagesCount = opening?.unreadInConversationApplicants.pagination.totalCount || 0;
  const unreadShortlistApplicants = opening?.unreadShortlistApplicants.pagination.totalCount || 0;

  const isOpeningInactive = isInactive(opening?.state);

  useInboxMessageThreadUpdatedSubscription({
    onSubscriptionData: data => {
      if (data.subscriptionData.data?.messageThreadUpdated.updatedMessageThread.opening?.id === openingId) {
        refetchOpeningApplicantCounts();
      }
    },
  });

  useEffect(() => {
    if (openingId) {
      getOpeningApplicantCounts({
        variables: {
          id: openingId,
        },
      });
    }
  }, [openingId]);

  useEffect(() => {
    if (openingApplicantCountsData && isOpeningInactive) {
      history.replace(
        createLink({
          link: FUNNEL_ROUTE,
          params: {
            openingId: openingId,
          },
          queryParams: {
            activeView: ActiveView.PENDING_INVITES,
          },
        }),
      );
    }
  }, [openingApplicantCountsData]);

  useEffect(() => {
    trackEvent({
      context: MetricContext.OPENING_VIEW,
      interaction: MetricAction.VIEW_OPENING_PAGE,
      payload: {
        'Active View': activeView,
      },
    });
  }, [activeView]);

  return (
    <>
      {!isCompanyProfilePublished ? (
        <FinishCompanyProfileModal className="pipeline-talents__finish-profile-modal" />
      ) : null}

      <div className={classnames('opening-page', !isCompanyProfilePublished && 'opening-page--blur')}>
        <div className="opening-page__header">
          <Header
            isLoading={isOpeningApplicantCountsLoading}
            openingState={opening?.state}
            recommended={recommended}
            totalMessaged={totalMessaged}
            totalShortlist={totalShortlist}
            totalUnanswered={totalUnanswered}
            unreadMessagesCount={unreadMessagesCount}
            unreadShortlistApplicants={unreadShortlistApplicants}
          />
        </div>

        <div className="opening-page__body">
          {activeView === ActiveView.INVITE_CANDIDATES && opening && (
            <InviteCandidates isActionsDisabled={!isCompanyProfilePublished} opening={opening} />
          )}

          {activeView === ActiveView.PENDING_INVITES && opening && (
            <PendingInvites pendingInvitesCount={totalUnanswered} opening={opening} />
          )}

          {activeView === ActiveView.IN_SHORTLIST && opening && <InShortlist totalShortlist={totalShortlist} />}

          {activeView === ActiveView.IN_CONVERSATION && opening && (
            <InConversation unreadMessagesCount={unreadMessagesCount} />
          )}
        </div>
      </div>

      <OpeningCandidateProfile />
    </>
  );
};
